import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface DevicePreferences {
  videoDeviceId: string | null;
  audioInputDeviceId: string | null;
  audioOutputDeviceId: string | null;
}

const STORAGE_KEY = 'session-capture-state';

/**
 * Persists session state to sessionStorage so it survives page refresh
 * but not tab close. Stores user info and device preferences.
 */
@Injectable({ providedIn: 'root' })
export class SessionStateService {
  private readonly platformId = inject(PLATFORM_ID);

  readonly fullNames = signal<string[]>([]);
  readonly admitted = signal(false);

  private get storage(): Storage | null {
    return isPlatformBrowser(this.platformId) ? sessionStorage : null;
  }

  /** Restore all state from sessionStorage. Call once on app init. */
  restore(): void {
    const raw = this.storage?.getItem(STORAGE_KEY);
    if (!raw) return;

    try {
      const state = JSON.parse(raw);
      if (Array.isArray(state.fullNames) && state.fullNames.length > 0) {
        this.fullNames.set(state.fullNames);
      }
      if (state.admitted) this.admitted.set(true);
    } catch {
      // Corrupted storage, ignore
    }
  }

  setFullNames(names: string[]): void {
    this.fullNames.set(names);
    this.save();
  }

  /** Combined display name: "Alex Smith & Sally Mae" */
  readonly displayName = computed(() => this.fullNames().join(' & ') || 'Guest');

  setAdmitted(admitted: boolean): void {
    this.admitted.set(admitted);
    this.save();
  }

  getDevicePreferences(): DevicePreferences | null {
    const raw = this.storage?.getItem(STORAGE_KEY);
    if (!raw) return null;

    try {
      const state = JSON.parse(raw);
      return state.devices ?? null;
    } catch {
      return null;
    }
  }

  saveDevicePreferences(prefs: DevicePreferences): void {
    const current = this.loadState();
    current['devices'] = prefs;
    this.storage?.setItem(STORAGE_KEY, JSON.stringify(current));
  }

  private save(): void {
    const current = this.loadState();
    current['fullNames'] = this.fullNames();
    current['admitted'] = this.admitted();
    this.storage?.setItem(STORAGE_KEY, JSON.stringify(current));
  }

  private loadState(): Record<string, unknown> {
    const raw = this.storage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  clear(): void {
    this.fullNames.set([]);
    this.admitted.set(false);
    this.storage?.removeItem(STORAGE_KEY);
  }
}
